'use client';

import { NumberField as BaseNumberField } from '@connected/form-fields/components/number';
import { Button } from '@connected/ui/components/button';
import { ButtonGroup } from '@connected/ui/components/button-group';

import type {
    DeviceForm,
    DevicePatcher,
    RisoSign,
} from '../../types/hook-types';

export type RisoFieldCellProps = {
    form: DeviceForm;
    rowIndex: number;
    patcher: DevicePatcher;
    disabled: boolean;
};

const risoSignOptions: Record<NonNullable<RisoSign>, { label: string; description: string }> = {
    '>': { label: '>', description: 'Groter dan' },
    '<': { label: '<', description: 'Kleiner dan' }, // Deprecated
    '=': { label: '=', description: 'Gelijk aan' },
};

/**
 * Riso field with sign toggle button and number input.
 * Used in device tables for RCD, RCCB, and FCPD.
 */
export function RisoFieldCell({
    form,
    rowIndex,
    patcher,
    disabled,
}: RisoFieldCellProps) {
    return (
        <form.Subscribe
            selector={state => ({
                sign: state.values.devices[rowIndex]?.risoSign,
                value: state.values.devices[rowIndex]?.risoValue,
            })}
        >
            {({ sign, value }) => {
                const validSign: RisoSign = (sign === '>' || sign === '<' || sign === '=') ? sign : '>';
                const activeSignOption = risoSignOptions[validSign];

                function toggleSign() {
                    // '<' is deprecated: allow escaping to '>', but never cycle back to '<'
                    const newSign: RisoSign = (validSign === '<' || validSign === '>') ? '=' : '>';
                    form.setFieldValue(`devices[${rowIndex}].risoSign`, newSign);
                    patcher.patchField('risoSign')({ value: newSign });
                }

                return (
                    <ButtonGroup className="w-full min-w-[10ch]">
                        <Button
                            size="icon"
                            variant="outline"
                            className="h-10 gap-1 rounded-r-none border-r-0 px-3"
                            type="button"
                            onClick={toggleSign}
                            title={activeSignOption?.description}
                            disabled={disabled}
                        >
                            {activeSignOption?.label}
                        </Button>
                        <BaseNumberField
                            name={`devices[${rowIndex}].risoValue`}
                            onChange={(newValue) => {
                                form.setFieldValue(`devices[${rowIndex}].risoValue`, newValue);
                                patcher.patchField('risoValue')({ value: newValue });
                            }}
                            maxPrecision={2}
                            value={value ?? null}
                            className="flex-1 min-w-[3ch] rounded-none"
                            disabled={disabled}
                        />
                    </ButtonGroup>
                );
            }}
        </form.Subscribe>
    );
}
